import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Plus, FileText, ChevronRight, Calendar, Settings, BarChart3, Users, UserPlus, ChevronDown, ChevronUp, Copy, Trash2 } from 'lucide-react';
import { projectsApi, protocolsApi, templatesApi } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/LoadingScreen';
import { useTelegram } from '../hooks/useTelegram';
import { useAuthStore } from '../store/auth';
import { useLogger } from '../utils/logger';

export function ProtocolsPage() {
  const logger = useLogger('ProtocolsPage');
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hapticFeedback, showAlert, showConfirm } = useTelegram();
  const currentUser = useAuthStore((state) => state.user);

  useEffect(() => {
    logger.componentMount({ projectId });
    return () => {
      logger.componentUnmount();
    };
  }, [projectId]);

  const [isCreating, setIsCreating] = useState(false);
  const [showTeam, setShowTeam] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [newProtocol, setNewProtocol] = useState({
    title: '',
    level: 'BOTTOM' as 'TOP' | 'BOTTOM',
    meetingDate: format(new Date(), 'yyyy-MM-dd'),
    theme: '',
    chairman: '',
    selectedAttendees: [] as string[], // ID участников из команды проекта
  });
  const [newMember, setNewMember] = useState({
    position: '',
    firstName: '',
    lastName: '',
    username: '',
    role: 'EXECUTOR',
  });

  const { data: project, isLoading: projectLoading, error: projectError } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      logger.apiCall('GET', `/projects/${projectId}`);
      try {
        const data = await projectsApi.get(projectId!);
        logger.apiSuccess('GET', `/projects/${projectId}`, data);
        return data;
      } catch (error: any) {
        logger.apiError('GET', `/projects/${projectId}`, error);
        throw error;
      }
    },
    enabled: !!projectId,
    retry: 2,
  });

  // Получаем протоколы пользователя (все, где он участвует)
  const {
    data: userProtocols,
    isLoading: userProtocolsLoading,
    error: userProtocolsError,
    refetch: refetchUserProtocols,
  } = useQuery({
    queryKey: ['protocols', 'my'],
    queryFn: async () => {
      logger.apiCall('GET', '/protocols/my');
      try {
        const data = await protocolsApi.listMy();
        logger.apiSuccess('GET', '/protocols/my', data);
        return data;
      } catch (error: any) {
        // Fallback: если endpoint /protocols/my недоступен/ошибка — собираем по проектам пользователя
        logger.apiError('GET', '/protocols/my', error);
        try {
          const projects = await projectsApi.list();
          const protocolsByProject = await Promise.all(
            (projects || []).map(async (p: any) => {
              const list = await protocolsApi.list(p.id);
              return (list || []).map((prot: any) => ({
                ...prot,
                projectId: p.id,
                projectName: p.name,
              }));
            })
          );
          return protocolsByProject.flat();
        } catch (fallbackError: any) {
          // если даже fallback не сработал — пробрасываем исходную ошибку
          throw error;
        }
      }
    },
    retry: 2,
    staleTime: 30000, // 30 секунд
    placeholderData: [],
  });

  // Получаем протоколы проекта (если указан projectId)
  const {
    data: projectProtocols,
    isLoading: projectProtocolsLoading,
    error: projectProtocolsError,
    refetch: refetchProjectProtocols,
  } = useQuery({
    queryKey: ['protocols', projectId],
    queryFn: async () => {
      logger.apiCall('GET', `/projects/${projectId}/protocols`);
      try {
        const data = await protocolsApi.list(projectId!);
        logger.apiSuccess('GET', `/projects/${projectId}/protocols`, data);
        return data;
      } catch (error: any) {
        logger.apiError('GET', `/projects/${projectId}/protocols`, error);
        throw error;
      }
    },
    enabled: !!projectId && !!project, // Загружаем протоколы только если проект загружен
    retry: 2,
    keepPreviousData: true,
    staleTime: 30000, // 30 секунд
    placeholderData: [],
  });

  // Используем протоколы пользователя, если нет projectId, иначе протоколы проекта
  const protocols = projectId ? projectProtocols : userProtocols;
  const protocolsLoading = projectId ? projectProtocolsLoading : userProtocolsLoading;
  const protocolsError = projectId ? projectProtocolsError : userProtocolsError;
  const refetchProtocols = projectId ? refetchProjectProtocols : refetchUserProtocols;
  
  // Логируем для отладки
  useEffect(() => {
    if (!projectId) {
      logger.debug('User protocols data', {
        loading: userProtocolsLoading,
        hasData: !!userProtocols,
        count: userProtocols?.length || 0,
        protocols: userProtocols?.map((p: any) => ({
          id: p.id,
          title: p.title,
          projectName: p.projectName,
        })) || [],
      });
    }
  }, [userProtocols, userProtocolsLoading, projectId, logger]);

  const isLoading = (projectId ? projectLoading : false) || protocolsLoading;

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      logger.apiCall('POST', '/protocols', data);
      try {
        const result = await protocolsApi.create(data);
        logger.apiSuccess('POST', '/protocols', result);
        return result;
      } catch (error: any) {
        logger.apiError('POST', '/protocols', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      logger.userAction('protocol_created', { protocolId: data.id, projectId });
      queryClient.invalidateQueries({ queryKey: ['protocols', projectId] });
      setIsCreating(false);
      setNewProtocol({ title: '', level: 'BOTTOM', meetingDate: format(new Date(), 'yyyy-MM-dd'), theme: '', chairman: '', selectedAttendees: [] });
      setSelectedTemplateId(null);
      hapticFeedback('success');
      navigate(`/protocols/${data.id}`);
    },
    onError: (err: Error) => {
      logger.error('Protocol creation failed', err);
      showAlert(`Ошибка: ${err.message}`);
      hapticFeedback('error');
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: (data: { username?: string; firstName?: string; lastName?: string; position?: string; role: string }) =>
      projectsApi.addMember(projectId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      hapticFeedback('success');
      showAlert('Участник добавлен');
      setShowAddMember(false);
      setNewMember({ position: '', firstName: '', lastName: '', username: '', role: 'EXECUTOR' });
    },
    onError: (err: Error) => {
      hapticFeedback('error');
      showAlert(`Ошибка: ${err.message}`);
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => projectsApi.removeMember(projectId!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      hapticFeedback('success');
      showAlert('Участник удален из проекта');
    },
    onError: (err: Error) => {
      hapticFeedback('error');
      showAlert(`Ошибка: ${err.message}`);
    },
  });

  const updateMemberRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      projectsApi.updateMemberRole(projectId!, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      hapticFeedback('success');
    },
    onError: (err: Error) => {
      hapticFeedback('error');
      showAlert(`Ошибка: ${err.message}`);
    },
  });

  const roleLabels: Record<string, string> = {
    ADMIN: 'Админ',
    MANAGER: 'Руководитель',
    EXECUTOR: 'Исполнитель',
    OBSERVER: 'Наблюдатель',
  };

  const roleOptions = [
    { value: 'ADMIN', label: '👑 Администратор' },
    { value: 'MANAGER', label: '📋 Руководитель' },
    { value: 'EXECUTOR', label: '👷 Исполнитель' },
    { value: 'OBSERVER', label: '👁 Наблюдатель' },
  ];

  const handleCreateProtocol = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProtocol.title.trim() || !projectId) return;
    
    // Проверяем, что выбраны участники
    if (!newProtocol.selectedAttendees || newProtocol.selectedAttendees.length === 0) {
      showAlert('Необходимо выбрать хотя бы одного участника из команды проекта');
      hapticFeedback('error');
      return;
    }
    
    // Автоматически добавляем уровень в название
    const levelLabel = newProtocol.level === 'TOP' ? 'Верхний' : 'Нижний';
    const titleWithLevel = `${newProtocol.title.trim()} (${levelLabel})`;
    
    // Получаем имена выбранных участников
    const selectedMembers = project?.members?.filter((m) => 
      newProtocol.selectedAttendees.includes(m.id)
    ) || [];
    const attendeeNames = selectedMembers.map((m) => {
      const name = `${m.lastName || ''} ${m.firstName}`.trim();
      return name || `@${m.username || 'unknown'}`;
    });
    
    createMutation.mutate({
      projectId,
      title: titleWithLevel,
      level: newProtocol.level,
      meetingDate: newProtocol.meetingDate,
      theme: newProtocol.theme.trim() || undefined,
      chairman: newProtocol.chairman.trim() || undefined,
      attendees: attendeeNames.length > 0 ? attendeeNames : undefined,
      templateId: selectedTemplateId || undefined,
    });
  };

  const canCreate = project?.role && ['ADMIN', 'MANAGER'].includes(project.role);

  const { data: templates } = useQuery({
    queryKey: ['templates', projectId],
    queryFn: () => templatesApi.list(projectId!),
    enabled: !!projectId && !!canCreate,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Если есть projectId, но проект не загружен или ошибка - показываем ошибку
  if (projectId) {
    if (projectError) {
      return (
        <div className="p-4 text-center">
          <p className="text-red-500 mb-4">Ошибка загрузки проекта: {(projectError as Error).message}</p>
          <Button onClick={() => {
            hapticFeedback('light');
            navigate('/projects');
          }}>
            Вернуться к проектам
          </Button>
        </div>
      );
    }

    if (!project) {
      return (
        <div className="p-4 text-center">
          <p className="text-gray-500 mb-4">Проект не найден</p>
          <Button onClick={() => {
            hapticFeedback('light');
            navigate('/projects');
          }}>
            Вернуться к проектам
          </Button>
        </div>
      );
    }
  }

  return (
    <div className="p-4 space-y-4">
      {/* Заголовок */}
      <div>
        {projectId && project && (
          <p className="text-sm text-tg-hint">{project.name}</p>
        )}
        <div className="flex items-center justify-between mt-1">
          <h1 className="text-2xl font-bold text-tg-text">Протоколы</h1>
          {canCreate && projectId && (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  hapticFeedback('light');
                  navigate(`/projects/${projectId}/kpi`);
                }}
              >
                <BarChart3 className="w-4 h-4" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  hapticFeedback('light');
                  navigate(`/projects/${projectId}/settings`);
                }}
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={() => setIsCreating(true)} className="gap-1">
                <Plus className="w-4 h-4" />
                Новый
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Список протоколов - показываем ПЕРВЫМ, особенно когда нет projectId */}
      {!projectId && (
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-tg-text mb-3">Протоколы, в которых вы участвуете</h2>
        </div>
      )}
      
      {/* Ошибка загрузки (когда данных нет) */}
      {!protocolsLoading && protocolsError && (!protocols || protocols.length === 0) && (
        <Card className="p-4 border border-red-200 bg-red-50">
          <p className="text-sm text-red-700">
            Не удалось загрузить протоколы: {(protocolsError as any)?.message || 'Ошибка'}
          </p>
          <div className="mt-3">
            <Button
              size="sm"
              onClick={() => {
                hapticFeedback('light');
                refetchProtocols();
              }}
            >
              Повторить
            </Button>
          </div>
        </Card>
      )}

      {protocolsLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
          <p className="text-tg-hint ml-3">Загрузка протоколов...</p>
        </div>
      ) : protocols && protocols.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 mx-auto text-tg-hint mb-4" />
          <p className="text-tg-hint mb-4">
            {projectId ? 'В этом проекте пока нет протоколов' : 'У вас пока нет протоколов'}
          </p>
          {canCreate && projectId && (
            <Button onClick={() => setIsCreating(true)}>
              Создать первый протокол
            </Button>
          )}
        </div>
      ) : protocols && protocols.length > 0 ? (
        <div className="space-y-3">
          {/* Показываем предупреждение об ошибке, но не скрываем данные */}
          {protocolsError && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 text-sm">
                ⚠️ Ошибка обновления данных: {(protocolsError as any)?.message || 'Не удалось обновить протоколы'}
              </p>
            </div>
          )}
          {protocols.map((protocol: any) => (
            <Card
              key={protocol.id}
              variant="interactive"
              onClick={() => {
                hapticFeedback('light');
                navigate(`/protocols/${protocol.id}`);
              }}
              className="p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  {/* Название проекта - показываем только если нет projectId */}
                  {!projectId && protocol.projectName && (
                    <p className="text-xs font-medium text-blue-600 mb-1">
                      Проект: {protocol.projectName}
                    </p>
                  )}
                  <h3 className="font-semibold text-tg-text truncate">
                    {protocol.title}
                  </h3>
                  {protocol.theme && (
                    <p className="text-sm text-tg-hint mt-0.5 truncate">
                      {protocol.theme}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-tg-hint">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {format(new Date(protocol.meetingDate), 'd MMMM yyyy', { locale: ru })}
                    </span>
                    <span>
                      {protocol.tasksCount} задач{getTaskEnding(protocol.tasksCount)}
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-tg-hint flex-shrink-0" />
              </div>
            </Card>
          ))}
        </div>
      ) : null}

      {/* Команда проекта - показываем только если есть projectId, ПОСЛЕ списка протоколов */}
      {projectId && project && (
      <Card className="overflow-hidden">
        <button
          onClick={() => setShowTeam(!showTeam)}
          className="w-full p-4 flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-tg-button" />
            <span className="font-medium text-tg-text">
              Команда проекта ({project?.members?.length || 0})
            </span>
          </div>
          {showTeam ? (
            <ChevronUp className="w-5 h-5 text-tg-hint" />
          ) : (
            <ChevronDown className="w-5 h-5 text-tg-hint" />
          )}
        </button>

        {showTeam && (
          <div className="border-t border-tg-bg">
            {/* Список участников */}
            <div className="divide-y divide-tg-bg">
              {project?.members?.map((member) => {
                const isCurrentUser = currentUser && member.id === currentUser.id;
                const canManage =
                  project?.role === 'ADMIN' || project?.role === 'MANAGER';
                const canDelete = project?.role === 'ADMIN' && !isCurrentUser;

                return (
                  <div
                    key={member.id}
                    className="px-4 py-3 flex items-center justify-between gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      {member.position && (
                        <p className="text-xs font-medium text-blue-600">
                          {member.position}
                        </p>
                      )}
                      <p className="text-sm font-medium text-tg-text">
                        {member.lastName || ''} {member.firstName}
                      </p>
                      {member.username && (
                        <p className="text-xs text-tg-hint">@{member.username}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {canManage ? (
                        <Select
                          value={member.role}
                          onChange={(e) => {
                            const newRole = e.target.value;
                            if (newRole === member.role) return;
                            hapticFeedback('light');
                            updateMemberRoleMutation.mutate({
                              userId: member.id,
                              role: newRole,
                            });
                          }}
                          options={roleOptions}
                        />
                      ) : (
                        <Badge
                          variant={
                            member.role === 'ADMIN'
                              ? 'danger'
                              : member.role === 'MANAGER'
                              ? 'info'
                              : member.role === 'EXECUTOR'
                              ? 'success'
                              : 'default'
                          }
                          size="sm"
                        >
                          {roleLabels[member.role] || member.role}
                        </Badge>
                      )}

                      {canDelete && (
                        <button
                          onClick={async () => {
                            hapticFeedback('light');
                            const confirmed = await showConfirm(
                              `Удалить ${member.firstName} ${
                                member.lastName || ''
                              } из проекта?`,
                            );
                            if (confirmed) {
                              removeMemberMutation.mutate(member.id);
                            }
                          }}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Удалить из проекта"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {(!project?.members || project.members.length === 0) && (
                <div className="p-4 text-center text-tg-hint text-sm">
                  Нет участников
                </div>
              )}
            </div>

            {/* Форма добавления участника */}
            {canCreate && (
              <div className="p-4 border-t border-tg-bg">
                {showAddMember ? (
                  <div className="space-y-3">
                    <Input
                      label="Должность"
                      placeholder="Прораб, Инженер ПТО..."
                      value={newMember.position}
                      onChange={(e) => setNewMember({ ...newMember, position: e.target.value })}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        label="Фамилия"
                        placeholder="Иванов"
                        value={newMember.lastName}
                        onChange={(e) => setNewMember({ ...newMember, lastName: e.target.value })}
                      />
                      <Input
                        label="Имя *"
                        placeholder="Иван"
                        value={newMember.firstName}
                        onChange={(e) => setNewMember({ ...newMember, firstName: e.target.value })}
                      />
                    </div>
                    <div className="relative">
                      <Input
                        label="Telegram"
                        placeholder="@username"
                        value={newMember.username}
                        onChange={(e) => setNewMember({ ...newMember, username: e.target.value })}
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const text = await navigator.clipboard.readText();
                            if (text) {
                              const cleaned = text.replace('@', '').trim();
                              setNewMember({ ...newMember, username: cleaned });
                              hapticFeedback('success');
                              showAlert('Скопировано из буфера обмена');
                            }
                          } catch (err) {
                            hapticFeedback('error');
                            showAlert('Не удалось скопировать из буфера обмена');
                          }
                        }}
                        className="absolute right-2 top-8 p-1.5 text-tg-hint hover:text-tg-button rounded transition-colors"
                        title="Вставить из буфера обмена"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <Select
                      label="Права"
                      value={newMember.role}
                      onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                      options={roleOptions}
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          addMemberMutation.mutate({
                            position: newMember.position || undefined,
                            firstName: newMember.firstName || undefined,
                            lastName: newMember.lastName || undefined,
                            username: newMember.username.replace('@', '') || undefined,
                            role: newMember.role,
                          });
                        }}
                        disabled={!newMember.firstName.trim() && !newMember.username.trim()}
                        isLoading={addMemberMutation.isPending}
                        className="flex-1"
                        size="sm"
                      >
                        Добавить
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setShowAddMember(false);
                          setNewMember({ position: '', firstName: '', lastName: '', username: '', role: 'EXECUTOR' });
                        }}
                      >
                        Отмена
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddMember(true)}
                    className="w-full gap-1"
                  >
                    <UserPlus className="w-4 h-4" />
                    Добавить участника
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </Card>
      )}

      {/* Форма создания */}
      {isCreating && (
        <Card className="p-4 animate-slide-down">
          <form onSubmit={handleCreateProtocol} className="space-y-3">
            {/* Уровень протокола */}
            <div>
              <label className="block text-sm font-medium text-tg-hint mb-2">Уровень протокола</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setNewProtocol({ ...newProtocol, level: 'TOP' })}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    newProtocol.level === 'TOP' 
                      ? 'border-blue-500 bg-blue-50 text-blue-700' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg mb-1">🔵</div>
                  <div className="font-medium text-sm mb-1">Верхний</div>
                  <div className="text-xs text-tg-hint leading-tight">
                    Ген. директор,<br/>
                    Директор по строительству,<br/>
                    Нач. ПТО,<br/>
                    Нач. участка
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setNewProtocol({ ...newProtocol, level: 'BOTTOM' })}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    newProtocol.level === 'BOTTOM' 
                      ? 'border-green-500 bg-green-50 text-green-700' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg mb-1">🟢</div>
                  <div className="font-medium text-sm mb-1">Нижний</div>
                  <div className="text-xs text-tg-hint leading-tight">
                    Рук. проекта,<br/>
                    Нач. участка,<br/>
                    Прорабы,<br/>
                    Мастера
                  </div>
                </button>
              </div>
            </div>

            <div>
              <Input
                label="Название протокола"
                placeholder="Протокол совещания от..."
                value={newProtocol.title}
                onChange={(e) => setNewProtocol({ ...newProtocol, title: e.target.value })}
                autoFocus
              />
              <p className="text-xs text-tg-hint mt-1">
                Уровень будет добавлен автоматически: ({newProtocol.level === 'TOP' ? 'Верхний' : 'Нижний'})
              </p>
            </div>
            <Input
              type="date"
              label="Дата совещания"
              value={newProtocol.meetingDate}
              onChange={(e) => setNewProtocol({ ...newProtocol, meetingDate: e.target.value })}
            />
            
            {/* Выбор шаблона (опционально) */}
            {templates && templates.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-tg-text mb-2">
                  Шаблон протокола (опционально)
                </label>
                <Select
                  value={selectedTemplateId || ''}
                  onChange={(e) => {
                    const templateId = e.target.value || null;
                    setSelectedTemplateId(templateId);
                    if (templateId) {
                      const template = templates.find(t => t.id === templateId);
                      if (template) {
                        setNewProtocol({
                          ...newProtocol,
                          level: template.level,
                          theme: template.theme || '',
                          chairman: template.chairman || '',
                        });
                      }
                    }
                  }}
                  options={[
                    { value: '', label: 'Без шаблона (стандартные разделы)' },
                    ...templates.map(t => ({ value: t.id, label: t.name })),
                  ]}
                />
                {selectedTemplateId && (
                  <p className="text-xs text-tg-hint mt-1">
                    Будет создан протокол с разделами из шаблона
                  </p>
                )}
              </div>
            )}
            
            <Input
              label="Тема совещания (опционально)"
              placeholder="Общая организация и координация работ"
              value={newProtocol.theme}
              onChange={(e) => setNewProtocol({ ...newProtocol, theme: e.target.value })}
            />
            <Input
              label="Председатель (опционально)"
              placeholder="Иванов И.И."
              value={newProtocol.chairman}
              onChange={(e) => setNewProtocol({ ...newProtocol, chairman: e.target.value })}
            />
            
            {/* Выбор участников из команды проекта - ОБЯЗАТЕЛЬНО */}
            {project?.members && project.members.length > 0 ? (
              <div>
                <label className="block text-sm font-medium text-tg-text mb-2">
                  Участники протокола <span className="text-red-500">*</span>
                  <span className="text-xs text-tg-hint font-normal ml-1">(выберите из команды проекта)</span>
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {project.members.map((member) => {
                    const isSelected = newProtocol.selectedAttendees.includes(member.id);
                    return (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => {
                          hapticFeedback('light');
                          setNewProtocol({
                            ...newProtocol,
                            selectedAttendees: isSelected
                              ? newProtocol.selectedAttendees.filter(id => id !== member.id)
                              : [...newProtocol.selectedAttendees, member.id]
                          });
                        }}
                        className={`w-full text-left p-2 rounded-lg border-2 transition-all ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            {member.position && (
                              <p className="text-xs font-medium text-blue-600">{member.position}</p>
                            )}
                            <p className="text-sm font-medium text-tg-text">
                              {member.lastName || ''} {member.firstName}
                            </p>
                            {member.username && (
                              <p className="text-xs text-tg-hint">@{member.username}</p>
                            )}
                          </div>
                          {isSelected && (
                            <span className="text-blue-500 text-lg">✓</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {newProtocol.selectedAttendees.length > 0 ? (
                  <p className="text-xs text-green-600 mt-1 font-medium">
                    ✓ Выбрано: {newProtocol.selectedAttendees.length}
                  </p>
                ) : (
                  <p className="text-xs text-red-500 mt-1">
                    ⚠ Необходимо выбрать хотя бы одного участника
                  </p>
                )}
              </div>
            ) : (
              <div className="p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠ В проекте нет участников. Добавьте участников в команду проекта, чтобы создать протокол.
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <Button
                type="submit"
                isLoading={createMutation.isPending}
                disabled={!newProtocol.title.trim() || !newProtocol.selectedAttendees || newProtocol.selectedAttendees.length === 0}
                className="flex-1"
              >
                Создать
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setIsCreating(false);
                  setNewProtocol({ title: '', level: 'BOTTOM', meetingDate: format(new Date(), 'yyyy-MM-dd'), theme: '', chairman: '', selectedAttendees: [] });
                  setSelectedTemplateId(null);
                }}
              >
                Отмена
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}

function getTaskEnding(count: number): string {
  if (count % 10 === 1 && count % 100 !== 11) return 'а';
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return 'и';
  return '';
}

